// SlotEditor.tsx — Generic slot array editor with count-based ▲▼ UI
// Used by: RoughOpenings, Windows, ManDoors, DockLevelers, RectBlockouts,
//          RoundBlockouts, HorizFeatures, VertFeatures, BracePoints, PickPoints,
//          WeldConnections, Pilasters, Ledgers, etc.
// Slots are enabled/disabled by incrementing/decrementing the count.
// The `enabled` property is preserved in the data model for backward compatibility.

import React, { useState } from 'react';
import FeetInchesInput from '../components/FeetInchesInput';

export interface FieldDef {
  key: string;
  label: string;
  type: 'number' | 'text' | 'checkbox';
  unit?: string;
  min?: number;
  max?: number;
  step?: number;
}

export interface SlotEditorProps<T extends object> {
  slots: { enabled: boolean; data: T }[];
  fields: FieldDef[];
  maxSlots: number;
  onChange: (slots: { enabled: boolean; data: T }[]) => void;
  defaultData: () => T;
  label?: string;
  fieldActions?: Record<string, { label: string; getValue: (data: T) => unknown }>;
  renderExtra?: (origIdx: number, data: T, updateField: (key: string, value: unknown) => void) => React.ReactNode;
}

export function SlotEditor<T extends object>({
  slots, fields, maxSlots, onChange, defaultData, label = 'Slot',
  fieldActions, renderExtra,
}: SlotEditorProps<T>) {
  // Derive enabled list from data — backward compatible with saved projects
  const enabledIndices = slots.reduce<number[]>((acc, s, i) => {
    if (s.enabled) acc.push(i);
    return acc;
  }, []);
  const activeCount = enabledIndices.length;

  const [viewIdx, setViewIdx] = useState<number>(0);

  // Clamp viewIdx to valid range
  const clampedView = activeCount === 0 ? 0 : Math.min(viewIdx, activeCount - 1);

  function increment() {
    const next = [...slots];
    // Re-enable first disabled slot, or append new
    const disabledIdx = next.findIndex(s => !s.enabled);
    if (disabledIdx >= 0) {
      next[disabledIdx] = { ...next[disabledIdx], enabled: true };
    } else if (next.length < maxSlots) {
      next.push({ enabled: true, data: defaultData() });
    } else {
      return;
    }
    onChange(next);
    // Switch to the newly enabled slot
    const newEnabledCount = next.filter(s => s.enabled).length;
    setViewIdx(newEnabledCount - 1);
  }

  function decrement() {
    if (activeCount === 0) return;
    const next = [...slots];
    // Disable the last enabled slot
    const lastEnabled = enabledIndices[enabledIndices.length - 1];
    next[lastEnabled] = { ...next[lastEnabled], enabled: false };
    onChange(next);
    setViewIdx(Math.max(0, clampedView >= activeCount - 1 ? activeCount - 2 : clampedView));
  }

  function updateField(origIdx: number, key: string, value: unknown) {
    const next = [...slots];
    const d = { ...next[origIdx].data, [key]: value } as T;
    next[origIdx] = { ...next[origIdx], data: d };
    onChange(next);
  }

  const currentOrigIdx = enabledIndices[clampedView];
  const slot = currentOrigIdx !== undefined ? slots[currentOrigIdx] : undefined;

  return (
    <div className="slot-editor">
      {/* Counter bar */}
      <div className="slot-counter">
        <button
          className="slot-counter-btn"
          disabled={activeCount <= 0}
          onClick={decrement}
          title={`Remove last ${label}`}
        >&#x25BC;</button>
        <span className="slot-counter-label">
          {activeCount} {label}{activeCount !== 1 ? 's' : ''}
          <small style={{ opacity: 0.5, marginLeft: 4 }}>/ {maxSlots}</small>
        </span>
        <button
          className="slot-counter-btn"
          disabled={activeCount >= maxSlots}
          onClick={increment}
          title={`Add ${label}`}
        >&#x25B2;</button>
      </div>

      {activeCount === 0 ? (
        <div className="slot-empty">
          No {label.toLowerCase()}s &mdash; click &#x25B2; to add
        </div>
      ) : (
        <>
          {/* Tabs for enabled slots only */}
          <div className="slot-tabs">
            {enabledIndices.map((origI, vi) => (
              <button
                key={origI}
                className={`slot-tab${vi === clampedView ? ' active' : ''} enabled`}
                onClick={() => setViewIdx(vi)}
              >
                {label} {origI + 1}
              </button>
            ))}
          </div>

          {/* Active slot fields — no Enabled checkbox, slot is always enabled */}
          {slot && (
            <div className="slot-fields">
              {renderExtra && currentOrigIdx !== undefined && renderExtra(currentOrigIdx, slot.data, (key, value) => updateField(currentOrigIdx, key, value))}
              {fields.map(f => {
                const dataRec = slot.data as Record<string, unknown>;
                const isImperial = f.type === 'number' && (f.unit === '"' || f.unit === "'");
                return (
                  <div className="slot-row" key={f.key}>
                    <label className="slot-label">{f.label}</label>
                    {f.type === 'checkbox' ? (
                      <input
                        type="checkbox"
                        checked={!!dataRec[f.key]}
                        onChange={e => updateField(currentOrigIdx, f.key, e.target.checked)}
                      />
                    ) : isImperial ? (
                      <>
                        <FeetInchesInput
                          value={(dataRec[f.key] as number) ?? 0}
                          onChange={v => updateField(currentOrigIdx, f.key, v)}
                          unit={f.unit}
                          min={f.min}
                        />
                        {fieldActions?.[f.key] && (
                          <button
                            className="slot-action-btn"
                            title={fieldActions[f.key].label}
                            onClick={() => updateField(currentOrigIdx, f.key, fieldActions[f.key].getValue(slot.data))}
                          >{fieldActions[f.key].label}</button>
                        )}
                      </>
                    ) : (
                      <>
                        <input
                          className="slot-input"
                          type={f.type}
                          value={dataRec[f.key] as string | number}
                          min={f.min}
                          max={f.max}
                          step={f.step ?? (f.type === 'number' ? 0.5 : undefined)}
                          onChange={e => updateField(
                            currentOrigIdx,
                            f.key,
                            f.type === 'number' ? parseFloat(e.target.value) : e.target.value
                          )}
                        />
                        {f.unit && <span className="slot-unit">{f.unit}</span>}
                        {fieldActions?.[f.key] && (
                          <button
                            className="slot-action-btn"
                            title={fieldActions[f.key].label}
                            onClick={() => updateField(currentOrigIdx, f.key, fieldActions[f.key].getValue(slot.data))}
                          >{fieldActions[f.key].label}</button>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
